/**
 * @fileoverview Section Navigation Component
 *
 * Purpose: Sticky sidebar navigation for jumping between design system sections
 * Used by: Component library page
 */

import { cn } from "@/utils/misc";

interface SectionNavProps {
  sections: Array<{
    id: string;
    label: string;
    subsections?: Array<{ id: string; label: string }>;
  }>;
}

export function SectionNav({ sections }: SectionNavProps) {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 80; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <nav className="sticky top-24 h-fit space-y-1">
      <div className="mb-3 text-sm font-semibold text-foreground">
        On This Page
      </div>
      {sections.map((section) => (
        <div key={section.id}>
          <button
            onClick={() => scrollToSection(section.id)}
            className={cn(
              "block w-full rounded-md px-3 py-1.5 text-left text-sm transition-colors",
              "text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            {section.label}
          </button>
          {section.subsections && (
            <div className="ml-3 mt-1 space-y-1">
              {section.subsections.map((subsection) => (
                <button
                  key={subsection.id}
                  onClick={() => scrollToSection(subsection.id)}
                  className={cn(
                    "block w-full rounded-md px-3 py-1 text-left text-xs transition-colors",
                    "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                  )}
                >
                  {subsection.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </nav>
  );
}
